import { useDispatch } from 'react-redux'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  IconButtonProps,
  Tooltip
} from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { Delete as DeleteIcon } from '@mui/icons-material'
import { deleteSector } from '../redux/slices/sectors'
import { useTranslation } from 'react-i18next'
import { Project, Sector } from '../types'
import { useState } from 'react'
import { usePolicy } from '../contexts/PolicyContext'
import { ProjectStateType } from '../enums/ProjectStateType'

export interface SectorDeleteButtonProps extends Omit<IconButtonProps, 'onClick' | 'children'> {
  project?: Project
  sector: Sector
}

export default function SectorDeleteButton({ project, sector, ...props }: SectorDeleteButtonProps) {
  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The polcies.
   */
  const { canEditProject } = usePolicy()

  /**
   * Indicates if a sector is being deleted.
   */
  const [isDeleting, setIsDeleting] = useState<boolean>(false)

  /**
   * Indicates if the dialog is open.
   */
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

  /**
   * Open the dialog.
   */
  function openDialog(): void {
    setIsDialogOpen(true)
  }

  /**
   * Close the dialog.
   */
  function closeDialog(): void {
    setIsDialogOpen(false)
  }

  /**
   * Handle the delete action.
   */
  async function handleDelete(): Promise<void> {
    try {
      setIsDeleting(true)
      dispatch(deleteSector(sector.id))
    } catch (error: unknown) {
      console.error(error)
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <>
      {project && project.state !== ProjectStateType.ARCHIVED && canEditProject(project) && (
        <Tooltip title={t('delete')}>
          <IconButton color="error" size="small" {...props} onClick={() => openDialog()}>
            <DeleteIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}

      <Dialog open={isDialogOpen} maxWidth="sm" fullWidth onClose={() => closeDialog()}>
        <DialogTitle>{t('deleteSector')}</DialogTitle>
        <DialogContent>
          <Alert severity="error">{t('confirmDeleteSector')}</Alert>
        </DialogContent>
        <DialogActions sx={{ pt: 0 }}>
          <Button variant="text" color="inherit" onClick={() => closeDialog()}>
            {t('cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={isDeleting}
            onClick={handleDelete}
          >
            {t('confirm')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  )
}
